import { isSameDay } from 'date-fns/isSameDay';
import React, { useCallback, useRef, useState } from 'react';

import { getZonedDate } from '@eika-infrastruktur/date';
import { AttentionMessage } from '@eika/attention-message';
import { LinkButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { IconTypes } from '@eika/icon';

import type { AccountId, ApprovalStatus, RecurringPaymentAgreement } from '../../../generated-types';
import { useFormContext } from '../../../hooks/useForm';
import usePaymentAccount from '../../../hooks/usePaymentAccount';
import useTranslation, { format } from '../../../hooks/useTranslation';
import { isValidAccountTransferFrom, isValidTransferAgreementAccount } from '../../../services/accountFilters';
import SigningContextFlow from '../../../signingContext/SigningContextFlow';
import { useSigningContext } from '../../../signingContext/signingContext';
import { paymentTrackIDs } from '../../../utils/gtm/trackIDs/payment';
import { TransactionForm, TransactionSubmitWrapper } from '../../TransactionForm';
import { DismissKeyboardStickyFooter } from '../../shared/DismissKeyboardStickyFooter';
import { hasSigningError } from '../Payment/PaymentForm';
import DeleteRecurringPayment from '../RecurringPayment/DeleteRecurringPayment';
import RecurringPaymentUpdateInfo from '../RecurringPaymentUpdateInfo';
import InvalidFromAccountInfo, { TransactionType } from '../Transfer/InvalidFromAccountInfo';
import UpdateRetry from '../UpdateRetry';
import RecurringTransferFields from './RecurringTransferFields';
import { RecurringTransferAction, RecurringTransferActionType } from './form/actions';
import { RecurringTransferState } from './form/state';
import { RecurringTransferError } from './form/validate';
import updateRecurringTransferRequest from './updateRecurringTransferRequest';

import '../RecurringPayment/UpdateRecurringPayment.scss';

export interface Props {
    recurringTransfer: RecurringPaymentAgreement;
    paymentOperationPending: boolean;
    paymentStatus: ApprovalStatus | undefined;
    onClose: () => void;
}

export const ANALYTICS_CATEGORY = 'update-recurring-transfer';

const UpdateRecurringTransfer: React.FC<Props> = ({
    recurringTransfer,
    paymentOperationPending,
    paymentStatus,
    onClose,
}) => {
    const translated = useTranslation();
    const fromAccountId = useRef<AccountId>();
    const account = usePaymentAccount(isValidTransferAgreementAccount);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const signingRequest = useCallback(
        (state: RecurringTransferState) => updateRecurringTransferRequest(state, recurringTransfer.id),
        [recurringTransfer.id],
    );

    const { signingError, handleCancelSigningFlow, startSigning, showPaymentRetry } = useSigningContext(signingRequest);
    const { state, checkFormValidity, handleChange } = useFormContext<
        RecurringTransferState,
        RecurringTransferAction,
        RecurringTransferError
    >();

    const updateTransferAgreement = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (await checkFormValidity()) {
                startSigning(state);
            }
        },
        [checkFormValidity, state, startSigning],
    );

    const hasChangedEndDate =
        !recurringTransfer?.lastPaymentDate !== !state.lastPaymentDate ||
        (!!recurringTransfer?.lastPaymentDate &&
            !!state.lastPaymentDate &&
            !isSameDay(getZonedDate(recurringTransfer.lastPaymentDate), getZonedDate(state.lastPaymentDate)));

    const compareNullableStrings = (a: string | undefined, b: string | undefined) => (a ?? '') !== (b ?? '');

    const hasStateChange =
        recurringTransfer.amount !== state.amount ||
        compareNullableStrings(recurringTransfer.text, state.note) ||
        compareNullableStrings(recurringTransfer.ownText, state.ownText) ||
        recurringTransfer.paymentInterval !== state.paymentInterval ||
        (!!state.toAccount && recurringTransfer.toAccount.value !== state.toAccount.value) ||
        (!!state.fromAccount && recurringTransfer.fromAccountNumber.value !== state.fromAccount.value) ||
        !isSameDay(getZonedDate(recurringTransfer.nextPaymentDate), state.firstPaymentDate) ||
        hasChangedEndDate;

    if (showPaymentRetry && !showDeleteModal) {
        return <UpdateRetry onClose={onClose} recurringPaymentType={recurringTransfer.paymentType} />;
    }

    if (showDeleteModal) {
        return (
            <div className="delete-recurring-payment__result">
                <DeleteRecurringPayment
                    recurringPayment={recurringTransfer}
                    paymentOperationPending={paymentOperationPending}
                    paymentStatus={paymentStatus}
                    showDialogue={showDeleteModal}
                    closeDialogue={() => setShowDeleteModal(false)}
                />
            </div>
        );
    }

    if (signingError || paymentStatus === 'COMPLETED' || hasSigningError(paymentStatus)) {
        return (
            <RecurringPaymentUpdateInfo
                status={paymentStatus}
                error={signingError}
                retry={signingError ? handleCancelSigningFlow : onClose}
                fromAccountId={fromAccountId.current}
                onClose={onClose}
                recurringPaymentType={recurringTransfer.paymentType}
            />
        );
    }

    const isInvalidAccount =
        !!account &&
        !state.fromAccount &&
        !isValidAccountTransferFrom(account) &&
        isValidTransferAgreementAccount(account);

    if (isInvalidAccount && !state.showFromAccounts) {
        return (
            <InvalidFromAccountInfo
                onSelect={() => handleChange({ type: RecurringTransferActionType.SHOW_FROM_ACCOUNTS })}
                account={account}
                transactionType={TransactionType.TRANSFER}
            />
        );
    }

    const canUserDeleteTransfer = recurringTransfer.permissions?.canReject;

    return (
        <SigningContextFlow onClosePayment={onClose}>
            <h2>{translated.recurringPayments.recurringTransfer}</h2>
            <TransactionForm eightCol onSubmit={updateTransferAgreement} className="update-recurring-payment-form">
                <RecurringTransferFields />
                <TransactionSubmitWrapper>
                    {hasStateChange ? (
                        <div className="update-recurring-payment__update-container">
                            <ButtonWithSpinner
                                buttonType="positive"
                                className="update-recurring-payment__submit"
                                type="submit"
                                showSpinner={paymentOperationPending}
                                data-testid="update-recurring-transfer-button"
                                data-track-id={paymentTrackIDs.recurring.update}
                                data-track-linkname="submit"
                            >
                                {translated.recurringPayments.update}
                            </ButtonWithSpinner>
                        </div>
                    ) : (
                        <div className="update-recurring-payment__delete-button-row">
                            {canUserDeleteTransfer && (
                                <LinkButton
                                    onClick={() => setShowDeleteModal(true)}
                                    icon={IconTypes.SLETT_24}
                                    data-track-id={paymentTrackIDs.recurring.delete}
                                    data-track-linkname={translated.delete}
                                >
                                    {translated.delete}
                                </LinkButton>
                            )}
                        </div>
                    )}
                </TransactionSubmitWrapper>
            </TransactionForm>
            {!canUserDeleteTransfer && (
                <AttentionMessage
                    type="info"
                    icon={IconTypes.UTROPSTEGN_24}
                    text={format(
                        translated.recurringPayments.cantDeleteMessage,
                        translated.recurringPaymentType[recurringTransfer.paymentType],
                    )}
                />
            )}
            <DismissKeyboardStickyFooter />
        </SigningContextFlow>
    );
};

export default UpdateRecurringTransfer;
